import { ControlledTextField } from '@if/mui'
import { CardContent, CardHeader, Grid, Stack, Typography } from '@mui/material'
import { SelectComponent } from '@src/components'
import { CardBorderTop } from '@src/components/Card'
import { FinancingProgramProvider } from '@src/data/types/FinancingProgramProvider'
import { MechanicalWarrantyProduct } from '@src/data/types/MechanicalWarrantyProduct'
import { ChangeEvent, useEffect, useMemo } from 'react'
import { Control, UseFormSetValue, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CheckboxGroupField from '../../../../../components/CheckboxGroupField'
import { MechanicalWarrantyWorksheetDto } from '../../types/mechanicalWarrantyWorksheetSchema'

interface Props {
  mechanicalWarrantyProviders: FinancingProgramProvider[]
  mechanicalWarrantyProducts: MechanicalWarrantyProduct[]
  control: Control<MechanicalWarrantyWorksheetDto>
  setValue: UseFormSetValue<MechanicalWarrantyWorksheetDto>
}

const MechanicalWarrantyExtended = ({
  mechanicalWarrantyProviders,
  mechanicalWarrantyProducts,
  control,
  setValue
}: Props) => {
  const { t, i18n } = useTranslation()

  const providerId = useWatch({ control, name: 'providerId' }) ?? ''
  const mechanicalWarrantyProductId = useWatch({
    control,
    name: 'mechanicalWarrantyProductId'
  }) ?? ''

  // Auto-select first provider only if none is set (new worksheets)
  useEffect(() => {
    if (!providerId && mechanicalWarrantyProviders.length > 0) {
      setValue('providerId', mechanicalWarrantyProviders[0].id)
    }
  }, [providerId, mechanicalWarrantyProviders, setValue])

  const providerItems = useMemo(() => {
    return mechanicalWarrantyProviders.map((provider) => ({
      label: i18n.language === 'fr' ? provider.nameFr : provider.nameEn,
      value: provider.id,
    }))
  }, [mechanicalWarrantyProviders, i18n.language])

  const providerProducts = useMemo(() => {
    return mechanicalWarrantyProducts.map((product) => ({
      label: i18n.language === 'fr' ? product.nameFr : product.nameEn,
      value: product.id,
    }))
  }, [mechanicalWarrantyProducts, i18n.language])

  const selectedProduct = useMemo(() => {
    return mechanicalWarrantyProducts.find((p) => p.id === mechanicalWarrantyProductId)
  }, [mechanicalWarrantyProducts, mechanicalWarrantyProductId])

  const surchargeOptions = useMemo(() => {
    return (selectedProduct?.surcharges ?? []).map((s) => ({
      label: i18n.language === 'fr' ? s.nameFr : s.nameEn,
      value: s.id,
    }))
  }, [selectedProduct, i18n.language])

  const optionOptions = useMemo(() => {
    return (selectedProduct?.options ?? []).map((o) => ({
      label: i18n.language === 'fr' ? o.nameFr : o.nameEn,
      value: o.id,
    }))
  }, [selectedProduct, i18n.language])

  return (
    <CardBorderTop>
      <CardHeader title={t('mechanicalWarrantyWorksheet.extendedWarranty')} />
      <CardContent component={Stack} spacing={2}>
        <Typography variant="body1" fontWeight="bold">
          {t('mechanicalWarrantyWorksheet.providerLabel')}
        </Typography>
        <Grid sx={{ mb: 3 }}>
          <SelectComponent
            items={providerItems}
            name="mechanicalWarrantyWorksheet.providerLabel"
            value={providerId}
            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              setValue('providerId', e.target.value)
              setValue('mechanicalWarrantyProductId', '')
              setValue('additions', { options: [], surcharges: [] })
            }}
            label={t('mechanicalWarrantyWorksheet.providerLabel')}
          />
        </Grid>

        <Typography variant="body1" fontWeight="bold">
          {t('mechanicalWarrantyWorksheet.productLabel')}
        </Typography>
        <Grid sx={{ mb: 3 }}>
          <SelectComponent
            items={providerProducts}
            name="mechanicalWarrantyWorksheet.productLabel"
            value={mechanicalWarrantyProductId}
            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              setValue('mechanicalWarrantyProductId', e.target.value)
              setValue('additions', { options: [], surcharges: [] })
            }}
            label={t('mechanicalWarrantyWorksheet.productLabel')}
          />
        </Grid>
        <Stack>
          {surchargeOptions.length > 0 && (
            <>
              <Typography variant="subtitle2">{t('mechanicalWarrantyWorksheet.surcharge')}</Typography>
              <CheckboxGroupField
                name="additions.surcharges"
                control={control}
                options={surchargeOptions}
              />
            </>
          )}
          {optionOptions.length > 0 && (
            <>
              <Typography variant="subtitle2">{t('mechanicalWarrantyWorksheet.option')}</Typography>
              <CheckboxGroupField
                name="additions.options"
                control={control}
                options={optionOptions}
              />
            </>
          )}
        </Stack>
        <ControlledTextField
          label={t('mechanicalWarrantyWorksheet.policy')}
          control={control}
          name="policy"
          required
        />
      </CardContent>
    </CardBorderTop>
  )
}

export default MechanicalWarrantyExtended